import React, { createContext, useContext, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';

const GOALS_BY_IDS_QUERY = gql`
  query MatomoGoalsByIds($ids: [ID!]!) {
    matomoGoalsByIds(ids: $ids) {
      id
      name
    }
  }
`;

const MatomoGoalNamesContext = createContext({ goalNames: {}, loading: false });

export function isGoalDimension(dimension) {
  return dimension?.name === 'goal' || dimension?.dimension === 'goal' || dimension?.type === 'goal';
}

export function collectGoalIdsFromResults(results, dimensions) {
  if (!results?.length || !dimensions?.some(isGoalDimension)) {
    return [];
  }

  const goalDimensionNames = dimensions.filter(isGoalDimension).map(d => d.name);
  const ids = new Set();

  for (const result of results) {
    for (const row of result.data || []) {
      for (const name of goalDimensionNames) {
        if (row[name] != null) {
          ids.add(String(row[name]));
        }
      }
    }
  }

  return [...ids];
}

export function MatomoGoalNamesProvider({ results, dimensions, children }) {
  const goalIds = useMemo(
    () => collectGoalIdsFromResults(results, dimensions),
    [results, dimensions],
  );

  const { data, loading } = useQuery(GOALS_BY_IDS_QUERY, {
    variables: { ids: goalIds },
    skip: !goalIds.length,
  });

  const value = useMemo(() => {
    const goalNames = {};
    for (const goal of data?.matomoGoalsByIds || []) {
      goalNames[goal.id] = goal.name;
    }
    return { goalNames, loading };
  }, [data, loading]);

  return (
    <MatomoGoalNamesContext.Provider value={value}>
      {children}
    </MatomoGoalNamesContext.Provider>
  );
}

export function useMatomoGoalNames() {
  return useContext(MatomoGoalNamesContext);
}
